import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  MonthlyShiftSlotData,
  DailyShiftSlotGrouped,
  TimeSlot,
  ShiftSlotStatus,
  UrgentTarget,
  IncentiveSettingInput,
  SlotUrgentSettingInput,
} from '../../../../domain/dailyShiftSlot';
import type {
  DailyShiftSlotRepository,
  UpdateShiftSlotData,
} from '../../../../application/dailyShiftSlot';
import {
  fetchMonthlyShiftSlots,
  updateShiftSlots,
  updateSlotStatus,
  sendVacancyNotification,
  sendUrgentNotification,
  importShiftSlots,
  exportShiftSlots,
} from '../../../../application/dailyShiftSlot';
import { dailyShiftSlotApi, BalancedConfirmationResult } from '../../../../infrastructure/api/dailyShiftSlotApi';

/**
 * 表示可能な最小年（この年の1月が下限月）
 *
 * 急募一覧API（GET /api/daily-shift-slots/incentive-urgent-targets）が
 * year を 2026〜2999 でしか受け付けず、範囲外は HTTP 400 になるため、
 * 月ナビゲーション側でこれより前へ戻れないようにする。
 */
const MIN_YEAR = 2026;

/**
 * 月次シフト枠設定 状態型
 */
export interface MonthlyShiftSlotSetupState {
  data: MonthlyShiftSlotData | null;
  isLoading: boolean;
  error: string | null;
  currentYear: number;
  currentMonth: number;
  // 編集状態
  editedSlots: Map<string, Partial<TimeSlot>>;
  hasUnsavedChanges: boolean;
  // モーダル状態
  selectedDate: DailyShiftSlotGrouped | null;
  dateDetailModalOpen: boolean;
  doctorSelectDialogOpen: boolean;
  doctorSelectDialogType: 'vacancy' | 'urgent' | null;
  importDialogOpen: boolean;
  dateTimePickerDialogOpen: boolean;
  dateTimePickerDialogType: 'recruitmentStart' | 'deadline' | 'fixedRecruitmentStart' | null;
  fixedShiftConfirmDialogOpen: boolean;
  fixedShiftCloseDialogOpen: boolean;
  balancedConfirmDialogOpen: boolean;
  balancedPreview: BalancedConfirmationResult | null;
  fixedRecruitmentResult: { appliedShifts: number; matchedRules: number; skippedRules: number } | null;
  urgentSettingsModalOpen: boolean;
  copyDialogOpen: boolean;
  // 急募一覧・詳細
  urgentTargets: UrgentTarget[];
  urgentTargetsLoading: boolean;
  urgentTargetListModalOpen: boolean;
  selectedUrgentTarget: UrgentTarget | null;
  urgentTargetDetailDialogOpen: boolean;
}

/**
 * 月次シフト枠設定 Hook戻り値型
 */
export interface UseMonthlyShiftSlotSetupReturn extends MonthlyShiftSlotSetupState {
  // 月切り替え
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  goToMonth: (year: number, month: number) => void;
  /** 前月へ戻れるか（表示可能な下限月に達していないか） */
  canGoToPreviousMonth: boolean;
  // データ操作
  fetchData: () => Promise<void>;
  saveChanges: () => Promise<boolean>;
  // 枠編集
  updateSlot: (slotId: string, field: 'recruitmentSlots' | 'urgentSlots', value: number) => void;
  resetChanges: () => void;
  // コピー
  copySlotsToTargetDates: (targetDates: string[]) => Promise<boolean>;
  // ステータス操作
  startRecruitment: (recruitmentStartAt: string) => Promise<boolean>;
  closeRecruitment: (deadlineAt: string) => Promise<boolean>;
  openBalancedConfirmDialog: () => Promise<void>;
  closeBalancedConfirmDialog: () => void;
  executeBalancedConfirm: () => Promise<boolean>;
  startFixedRecruitment: (recruitmentStartAt: string) => Promise<boolean>;
  closeFixedRecruitment: () => Promise<boolean>;
  confirmFixedShifts: () => Promise<boolean>;
  clearFixedRecruitmentResult: () => void;
  // 通知送信
  sendVacancyNotificationToDoctor: (doctorIds: string[]) => Promise<boolean>;
  sendUrgentNotificationToDoctor: (doctorIds: string[]) => Promise<boolean>;
  // インポート/エクスポート
  handleImport: (csvData: string) => Promise<{ success: boolean; importedCount: number; errors: string[] }>;
  handleExport: () => Promise<void>;
  // モーダル操作
  openDateDetailModal: (date: DailyShiftSlotGrouped) => void;
  closeDateDetailModal: () => void;
  openDoctorSelectDialog: (type: 'vacancy' | 'urgent') => void;
  closeDoctorSelectDialog: () => void;
  openImportDialog: () => void;
  closeImportDialog: () => void;
  openDateTimePickerDialog: (type: 'recruitmentStart' | 'deadline' | 'fixedRecruitmentStart') => void;
  closeDateTimePickerDialog: () => void;
  openFixedShiftConfirmDialog: () => void;
  closeFixedShiftConfirmDialog: () => void;
  openFixedShiftCloseDialog: () => void;
  closeFixedShiftCloseDialog: () => void;
  openUrgentSettingsModal: () => void;
  closeUrgentSettingsModal: () => void;
  openCopyDialog: () => void;
  closeCopyDialog: () => void;
  // 急募一覧・詳細
  fetchUrgentTargets: () => Promise<void>;
  openUrgentTargetListModal: () => void;
  closeUrgentTargetListModal: () => void;
  openUrgentTargetDetail: (target: UrgentTarget) => void;
  closeUrgentTargetDetail: () => void;
  confirmDoctorDirectly: (doctorId: number) => Promise<boolean>;
  cancelUrgentTarget: () => Promise<boolean>;
  cancelApplication: (doctorId: number) => Promise<boolean>;
  // 急募設定保存
  saveIncentiveSettings: (settings: IncentiveSettingInput) => Promise<boolean>;
  saveSelectedUrgentTargetSettings: (settings: SlotUrgentSettingInput) => Promise<boolean>;
  // ユーティリティ
  clearError: () => void;
}

/**
 * 月次シフト枠設定管理用カスタムフック
 */
export const useMonthlyShiftSlotSetup = (
  repository: DailyShiftSlotRepository
): UseMonthlyShiftSlotSetupReturn => {
  const [searchParams] = useSearchParams();
  const now = new Date();

  // URLパラメータから年月を取得、なければ現在の日付
  const yearParam = searchParams.get('year');
  const monthParam = searchParams.get('month');
  const initialYear = yearParam ? parseInt(yearParam, 10) : now.getFullYear();
  const initialMonth = monthParam ? parseInt(monthParam, 10) : now.getMonth() + 1;

  const [state, setState] = useState<MonthlyShiftSlotSetupState>({
    data: null,
    isLoading: false,
    error: null,
    currentYear: initialYear,
    currentMonth: initialMonth,
    editedSlots: new Map(),
    hasUnsavedChanges: false,
    selectedDate: null,
    dateDetailModalOpen: false,
    doctorSelectDialogOpen: false,
    doctorSelectDialogType: null,
    importDialogOpen: false,
    dateTimePickerDialogOpen: false,
    dateTimePickerDialogType: null,
    fixedShiftConfirmDialogOpen: false,
    fixedShiftCloseDialogOpen: false,
    balancedConfirmDialogOpen: false,
    balancedPreview: null,
    fixedRecruitmentResult: null,
    urgentSettingsModalOpen: false,
    copyDialogOpen: false,
    urgentTargets: [],
    urgentTargetsLoading: false,
    urgentTargetListModalOpen: false,
    selectedUrgentTarget: null,
    urgentTargetDetailDialogOpen: false,
  });

  /**
   * データ取得
   */
  const fetchData = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const { data } = await fetchMonthlyShiftSlots(repository, {
        year: state.currentYear,
        month: state.currentMonth,
      });

      setState(prev => ({
        ...prev,
        data,
        isLoading: false,
        editedSlots: new Map(),
        hasUnsavedChanges: false,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'データ取得に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, [repository, state.currentYear, state.currentMonth]);

  /**
   * 初回データ取得
   */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * 前月へ戻れるか
   *
   * 下限月（MIN_YEAR年1月）に達している場合は戻れない。
   */
  const canGoToPreviousMonth =
    state.currentYear > MIN_YEAR ||
    (state.currentYear === MIN_YEAR && state.currentMonth > 1);

  /**
   * 前月へ
   *
   * 下限月より前へは遷移しない（急募一覧APIが year >= MIN_YEAR のみ受け付けるため）。
   */
  const goToPreviousMonth = useCallback(() => {
    setState(prev => {
      let year = prev.currentYear;
      let month = prev.currentMonth - 1;
      if (month < 1) {
        month = 12;
        year -= 1;
      }
      if (year < MIN_YEAR) {
        return prev;
      }
      return { ...prev, currentYear: year, currentMonth: month };
    });
  }, []);

  /**
   * 次月へ
   */
  const goToNextMonth = useCallback(() => {
    setState(prev => {
      let year = prev.currentYear;
      let month = prev.currentMonth + 1;
      if (month > 12) {
        month = 1;
        year += 1;
      }
      return { ...prev, currentYear: year, currentMonth: month };
    });
  }, []);

  /**
   * 指定月へ
   */
  const goToMonth = useCallback((year: number, month: number) => {
    setState(prev => ({ ...prev, currentYear: year, currentMonth: month }));
  }, []);

  /**
   * スロット更新（ローカル）
   */
  const updateSlot = useCallback((
    slotId: string,
    field: 'recruitmentSlots' | 'urgentSlots',
    value: number
  ) => {
    setState(prev => {
      const newEditedSlots = new Map(prev.editedSlots);
      const existing = newEditedSlots.get(slotId) || {};
      newEditedSlots.set(slotId, { ...existing, [field]: value });
      return {
        ...prev,
        editedSlots: newEditedSlots,
        hasUnsavedChanges: true,
      };
    });
  }, []);

  /**
   * 変更をリセット
   */
  const resetChanges = useCallback(() => {
    setState(prev => ({
      ...prev,
      editedSlots: new Map(),
      hasUnsavedChanges: false,
    }));
  }, []);

  /**
   * 変更を保存
   */
  const saveChanges = useCallback(async (): Promise<boolean> => {
    if (state.editedSlots.size === 0) {
      return true;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const slots: UpdateShiftSlotData[] = Array.from(state.editedSlots.entries()).map(
        ([id, changes]) => ({
          id,
          recruitmentSlots: changes.recruitmentSlots,
          urgentSlots: changes.urgentSlots,
        })
      );

      const result = await updateShiftSlots(repository, { slots });

      if (!result.success) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: result.errors?.join('\n') || '保存に失敗しました',
        }));
        return false;
      }

      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '保存に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [repository, state.editedSlots, fetchData]);

  /**
   * コピー元日付（selectedDate）の募集枠・急募枠を指定日付へコピー
   *
   * - 同じ時間帯（startTime-endTime）のスロットIDにマッピングして一括更新
   * - 診療予約枠・確定枠・不足枠は自動計算されるためコピー対象外
   */
  const copySlotsToTargetDates = useCallback(async (targetDates: string[]): Promise<boolean> => {
    const sourceDate = state.selectedDate;
    const days = state.data?.days;

    if (!sourceDate || !days || targetDates.length === 0) {
      return false;
    }

    // コピー元の時間帯ごとの値マップを作成
    const sourceValues = new Map<string, { recruitmentSlots: number; urgentSlots: number }>();
    sourceDate.slots.forEach(slot => {
      sourceValues.set(`${slot.startTime}-${slot.endTime}`, {
        recruitmentSlots: slot.recruitmentSlots,
        urgentSlots: slot.urgentSlots,
      });
    });

    // コピー先日付の同じ時間帯スロットへ更新データを作成
    const targetDateSet = new Set(targetDates);
    const slots: UpdateShiftSlotData[] = [];
    days
      .filter(day => targetDateSet.has(day.date))
      .forEach(day => {
        day.slots.forEach(slot => {
          const source = sourceValues.get(`${slot.startTime}-${slot.endTime}`);
          if (source) {
            slots.push({
              id: slot.id,
              recruitmentSlots: source.recruitmentSlots,
              urgentSlots: source.urgentSlots,
            });
          }
        });
      });

    if (slots.length === 0) {
      setState(prev => ({ ...prev, error: 'コピー先のシフト枠が見つかりませんでした' }));
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await updateShiftSlots(repository, { slots });

      if (!result.success) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: result.errors?.join('\n') || 'コピーに失敗しました',
        }));
        return false;
      }

      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'コピーに失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [repository, state.selectedDate, state.data, fetchData]);

  /**
   * 募集開始
   */
  const startRecruitment = useCallback(async (recruitmentStartAt: string): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await updateSlotStatus(repository, {
        year: state.currentYear,
        month: state.currentMonth,
        status: 'recruiting' as ShiftSlotStatus,
        recruitmentStartAt,
      });

      if (!result.success) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: result.errors?.join('\n') || '募集開始に失敗しました',
        }));
        return false;
      }

      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '募集開始に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [repository, state.currentYear, state.currentMonth, fetchData]);

  /**
   * 締切
   */
  const closeRecruitment = useCallback(async (deadlineAt: string): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await updateSlotStatus(repository, {
        year: state.currentYear,
        month: state.currentMonth,
        status: 'closed' as ShiftSlotStatus,
        deadlineAt,
      });

      if (!result.success) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: result.errors?.join('\n') || '締切に失敗しました',
        }));
        return false;
      }

      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '締切に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [repository, state.currentYear, state.currentMonth, fetchData]);

  /**
   * 確定
   */
  /**
   * バランス確定プレビューを取得してダイアログを開く（DB無変更）
   */
  const openBalancedConfirmDialog = useCallback(async (): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const preview = await dailyShiftSlotApi.confirmPreview(
        state.currentYear,
        state.currentMonth
      );

      setState(prev => ({
        ...prev,
        isLoading: false,
        balancedPreview: preview,
        balancedConfirmDialogOpen: true,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '確定プレビューの取得に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, [state.currentYear, state.currentMonth]);

  const closeBalancedConfirmDialog = useCallback(() => {
    setState(prev => ({
      ...prev,
      balancedConfirmDialogOpen: false,
      balancedPreview: null,
    }));
  }, []);

  /**
   * バランス確定（本確定）
   *
   * プレビューと同一の配分を適用し closed → confirmed へ遷移する。
   */
  const executeBalancedConfirm = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await dailyShiftSlotApi.confirmBalanced(
        state.currentYear,
        state.currentMonth
      );

      setState(prev => ({
        ...prev,
        isLoading: false,
        balancedConfirmDialogOpen: false,
        balancedPreview: null,
      }));
      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'シフトの確定に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [state.currentYear, state.currentMonth, fetchData]);

  /**
   * 固定シフト募集開始
   *
   * バックエンドで医師シフトルール展開 → DoctorShift 作成 → status を fixed_recruiting へ。
   */
  const startFixedRecruitment = useCallback(async (recruitmentStartAt: string): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await dailyShiftSlotApi.startFixedRecruitment(
        state.currentYear,
        state.currentMonth,
        recruitmentStartAt
      );

      setState(prev => ({
        ...prev,
        isLoading: false,
        fixedRecruitmentResult: result,
      }));
      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '固定シフト募集の開始に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [state.currentYear, state.currentMonth, fetchData]);

  /**
   * 固定シフト締切
   *
   * 固定シフト募集（医師の不可申告受付）を締め切り、status を fixed_closed へ。
   */
  const closeFixedRecruitment = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await dailyShiftSlotApi.closeFixedRecruitment(
        state.currentYear,
        state.currentMonth
      );

      setState(prev => ({ ...prev, isLoading: false }));
      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '固定シフト募集の締切に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [state.currentYear, state.currentMonth, fetchData]);

  /**
   * 固定シフト確定
   *
   * source=rule の医師シフトのみ確定し、日次枠の confirmed_slots に加算。
   */
  const confirmFixedShifts = useCallback(async (): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await dailyShiftSlotApi.confirmFixedShifts(
        state.currentYear,
        state.currentMonth
      );

      setState(prev => ({ ...prev, isLoading: false }));
      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '固定シフトの確定に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [state.currentYear, state.currentMonth, fetchData]);

  const openFixedShiftConfirmDialog = useCallback(() => {
    setState(prev => ({ ...prev, fixedShiftConfirmDialogOpen: true }));
  }, []);

  const closeFixedShiftConfirmDialog = useCallback(() => {
    setState(prev => ({ ...prev, fixedShiftConfirmDialogOpen: false }));
  }, []);

  const openFixedShiftCloseDialog = useCallback(() => {
    setState(prev => ({ ...prev, fixedShiftCloseDialogOpen: true }));
  }, []);

  const closeFixedShiftCloseDialog = useCallback(() => {
    setState(prev => ({ ...prev, fixedShiftCloseDialogOpen: false }));
  }, []);

  const clearFixedRecruitmentResult = useCallback(() => {
    setState(prev => ({ ...prev, fixedRecruitmentResult: null }));
  }, []);

  /**
   * 空き枠紹介通知送信
   */
  const sendVacancyNotificationToDoctor = useCallback(async (doctorIds: string[]): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await sendVacancyNotification(repository, {
        year: state.currentYear,
        month: state.currentMonth,
        doctorIds,
      });

      setState(prev => ({ ...prev, isLoading: false }));

      if (!result.success) {
        setState(prev => ({
          ...prev,
          error: result.errors?.join('\n') || '通知送信に失敗しました',
        }));
        return false;
      }

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '通知送信に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [repository, state.currentYear, state.currentMonth]);

  /**
   * 急募枠通知送信
   */
  const sendUrgentNotificationToDoctor = useCallback(async (doctorIds: string[]): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await sendUrgentNotification(repository, {
        year: state.currentYear,
        month: state.currentMonth,
        doctorIds,
      });

      setState(prev => ({ ...prev, isLoading: false }));

      if (!result.success) {
        setState(prev => ({
          ...prev,
          error: result.errors?.join('\n') || '急募通知送信に失敗しました',
        }));
        return false;
      }

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '急募通知送信に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [repository, state.currentYear, state.currentMonth]);

  /**
   * CSVインポート
   */
  const handleImport = useCallback(async (csvData: string): Promise<{
    success: boolean;
    importedCount: number;
    errors: string[];
  }> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await importShiftSlots(repository, {
        year: state.currentYear,
        month: state.currentMonth,
        csvData,
      });

      if (result.success) {
        await fetchData();
      }

      setState(prev => ({ ...prev, isLoading: false }));
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'インポートに失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return { success: false, importedCount: 0, errors: [errorMessage] };
    }
  }, [repository, state.currentYear, state.currentMonth, fetchData]);

  /**
   * CSVエクスポート
   */
  const handleExport = useCallback(async (): Promise<void> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await exportShiftSlots(repository, {
        year: state.currentYear,
        month: state.currentMonth,
      });

      if (!result.success) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: result.errors?.join('\n') || 'エクスポートに失敗しました',
        }));
        return;
      }

      // CSVダウンロード（Excel が UTF-8 と認識できるよう BOM を付与）
      const BOM = '\uFEFF';
      const csvWithBom = result.csvData.startsWith(BOM) ? result.csvData : BOM + result.csvData;
      const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setState(prev => ({ ...prev, isLoading: false }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'エクスポートに失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
    }
  }, [repository, state.currentYear, state.currentMonth]);

  /**
   * 日付詳細モーダルを開く
   */
  const openDateDetailModal = useCallback((date: DailyShiftSlotGrouped) => {
    setState(prev => ({
      ...prev,
      selectedDate: date,
      dateDetailModalOpen: true,
    }));
  }, []);

  /**
   * 日付詳細モーダルを閉じる
   */
  const closeDateDetailModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedDate: null,
      dateDetailModalOpen: false,
    }));
  }, []);

  /**
   * 医師選択ダイアログを開く
   */
  const openDoctorSelectDialog = useCallback((type: 'vacancy' | 'urgent') => {
    setState(prev => ({
      ...prev,
      doctorSelectDialogOpen: true,
      doctorSelectDialogType: type,
    }));
  }, []);

  /**
   * 医師選択ダイアログを閉じる
   */
  const closeDoctorSelectDialog = useCallback(() => {
    setState(prev => ({
      ...prev,
      doctorSelectDialogOpen: false,
      doctorSelectDialogType: null,
    }));
  }, []);

  /**
   * インポートダイアログを開く
   */
  const openImportDialog = useCallback(() => {
    setState(prev => ({ ...prev, importDialogOpen: true }));
  }, []);

  /**
   * インポートダイアログを閉じる
   */
  const closeImportDialog = useCallback(() => {
    setState(prev => ({ ...prev, importDialogOpen: false }));
  }, []);

  /**
   * 日時選択ダイアログを開く
   */
  const openDateTimePickerDialog = useCallback((
    type: 'recruitmentStart' | 'deadline' | 'fixedRecruitmentStart'
  ) => {
    setState(prev => ({
      ...prev,
      dateTimePickerDialogOpen: true,
      dateTimePickerDialogType: type,
    }));
  }, []);

  /**
   * 日時選択ダイアログを閉じる
   */
  const closeDateTimePickerDialog = useCallback(() => {
    setState(prev => ({
      ...prev,
      dateTimePickerDialogOpen: false,
      dateTimePickerDialogType: null,
    }));
  }, []);

  /**
   * 急募枠設定モーダルを開く
   */
  const openUrgentSettingsModal = useCallback(() => {
    setState(prev => ({ ...prev, urgentSettingsModalOpen: true }));
  }, []);

  /**
   * 急募枠設定モーダルを閉じる
   */
  const closeUrgentSettingsModal = useCallback(() => {
    setState(prev => ({ ...prev, urgentSettingsModalOpen: false }));
  }, []);

  /**
   * コピーダイアログを開く
   */
  const openCopyDialog = useCallback(() => {
    setState(prev => ({ ...prev, copyDialogOpen: true }));
  }, []);

  /**
   * コピーダイアログを閉じる
   */
  const closeCopyDialog = useCallback(() => {
    setState(prev => ({ ...prev, copyDialogOpen: false }));
  }, []);

  /**
   * 急募対象枠一覧取得
   */
  const fetchUrgentTargets = useCallback(async (): Promise<void> => {
    setState(prev => ({ ...prev, urgentTargetsLoading: true, error: null }));

    try {
      const targets = await dailyShiftSlotApi.getIncentiveUrgentTargets(
        state.currentYear,
        state.currentMonth
      );

      setState(prev => ({
        ...prev,
        urgentTargets: targets,
        urgentTargetsLoading: false,
        // 詳細表示中の枠があれば最新データに差し替える
        selectedUrgentTarget: prev.selectedUrgentTarget
          ? targets.find(target => target.id === prev.selectedUrgentTarget?.id) ?? prev.selectedUrgentTarget
          : null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '急募対象枠の取得に失敗しました';
      setState(prev => ({
        ...prev,
        urgentTargetsLoading: false,
        error: errorMessage,
      }));
    }
  }, [state.currentYear, state.currentMonth]);

  /**
   * 急募一覧モーダル表示中の一覧取得
   *
   * モーダルを開いた時の初回取得と、表示中に年月が切り替わった時の再取得を
   * この経路へ一本化する。fetchUrgentTargets は currentYear / currentMonth を
   * 依存に持つため、年月が変わると関数の同一性が変わり再取得が走る。
   */
  useEffect(() => {
    if (!state.urgentTargetListModalOpen) {
      return;
    }
    fetchUrgentTargets();
  }, [state.urgentTargetListModalOpen, fetchUrgentTargets]);

  /**
   * 急募一覧モーダルを開く（表示状態の変更のみ。一覧取得は上記 useEffect が担う）
   */
  const openUrgentTargetListModal = useCallback(() => {
    setState(prev => ({ ...prev, urgentTargetListModalOpen: true }));
  }, []);

  /**
   * 急募一覧モーダルを閉じる
   */
  const closeUrgentTargetListModal = useCallback(() => {
    setState(prev => ({ ...prev, urgentTargetListModalOpen: false }));
  }, []);

  /**
   * 急募詳細ダイアログを開く
   */
  const openUrgentTargetDetail = useCallback((target: UrgentTarget) => {
    setState(prev => ({
      ...prev,
      selectedUrgentTarget: target,
      urgentTargetDetailDialogOpen: true,
    }));
  }, []);

  /**
   * 急募詳細ダイアログを閉じる
   */
  const closeUrgentTargetDetail = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedUrgentTarget: null,
      urgentTargetDetailDialogOpen: false,
    }));
  }, []);

  /**
   * 医師の直接確定（応募中の医師より優先して確定）
   *
   * ※ バックエンドの直接確定エンドポイントは新設予定のため、実装されるまではエラーになる
   */
  const confirmDoctorDirectly = useCallback(async (doctorId: number): Promise<boolean> => {
    const target = state.selectedUrgentTarget;
    if (!target) {
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await dailyShiftSlotApi.confirmDoctorDirect(target.id, doctorId);

      setState(prev => ({ ...prev, isLoading: false }));
      await fetchUrgentTargets();
      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '医師の直接確定に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [state.selectedUrgentTarget, fetchUrgentTargets, fetchData]);

  /**
   * 急募キャンセル（対象枠の急募医師枠数を0に更新）
   */
  const cancelUrgentTarget = useCallback(async (): Promise<boolean> => {
    const target = state.selectedUrgentTarget;
    if (!target) {
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await updateShiftSlots(repository, {
        slots: [{ id: target.id, urgentSlots: 0 }],
      });

      if (!result.success) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: result.errors?.join('\n') || '急募のキャンセルに失敗しました',
        }));
        return false;
      }

      setState(prev => ({
        ...prev,
        isLoading: false,
        selectedUrgentTarget: null,
        urgentTargetDetailDialogOpen: false,
      }));
      await fetchUrgentTargets();
      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '急募のキャンセルに失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [repository, state.selectedUrgentTarget, fetchUrgentTargets, fetchData]);

  /**
   * 応募キャンセル（対象医師の医師シフトを削除）
   *
   * 削除後は募集済み枠数などが変わるため一覧と月次データを再取得する。
   * 詳細ダイアログは開いたままにする（続けて別の応募も取り消せるようにするため）。
   */
  const cancelApplication = useCallback(async (doctorId: number): Promise<boolean> => {
    const target = state.selectedUrgentTarget;
    if (!target) {
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await dailyShiftSlotApi.cancelApplication(target.id, doctorId);

      setState(prev => ({ ...prev, isLoading: false }));
      // fetchUrgentTargets が selectedUrgentTarget を最新データへ差し替える
      await fetchUrgentTargets();
      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '応募のキャンセルに失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [state.selectedUrgentTarget, fetchUrgentTargets, fetchData]);

  /**
   * 詳細ダイアログで表示中の枠へ急募設定を保存する
   *
   * 一括版（saveIncentiveSettings）と違い急募枠数は変更されない。
   * 通知文面は呼び出し側で変数置換を済ませたものを渡すこと。
   */
  const saveSelectedUrgentTargetSettings = useCallback(async (
    settings: SlotUrgentSettingInput
  ): Promise<boolean> => {
    const target = state.selectedUrgentTarget;
    if (!target) {
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await dailyShiftSlotApi.saveSlotUrgentSettings(target.id, settings);

      setState(prev => ({ ...prev, isLoading: false }));
      await fetchUrgentTargets();
      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '急募設定の保存に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [state.selectedUrgentTarget, fetchUrgentTargets, fetchData]);

  /**
   * 急募設定を保存する（インセンティブ・対象枠・LINE通知設定をまとめて保存）
   *
   * ※ バックエンドの保存エンドポイントは新設予定のため、実装されるまではエラーになる
   */
  const saveIncentiveSettings = useCallback(async (settings: IncentiveSettingInput): Promise<boolean> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await dailyShiftSlotApi.saveUrgentSettings(settings);

      setState(prev => ({ ...prev, isLoading: false }));
      await fetchUrgentTargets();
      await fetchData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '急募設定の保存に失敗しました';
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      return false;
    }
  }, [fetchUrgentTargets, fetchData]);

  /**
   * エラーをクリア
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    // 状態
    ...state,

    // 月切り替え
    goToPreviousMonth,
    goToNextMonth,
    goToMonth,
    canGoToPreviousMonth,

    // データ操作
    fetchData,
    saveChanges,

    // 枠編集
    updateSlot,
    resetChanges,

    // コピー
    copySlotsToTargetDates,

    // ステータス操作
    startRecruitment,
    closeRecruitment,
    openBalancedConfirmDialog,
    closeBalancedConfirmDialog,
    executeBalancedConfirm,
    startFixedRecruitment,
    closeFixedRecruitment,
    confirmFixedShifts,
    clearFixedRecruitmentResult,

    // 通知送信
    sendVacancyNotificationToDoctor,
    sendUrgentNotificationToDoctor,

    // インポート/エクスポート
    handleImport,
    handleExport,

    // モーダル操作
    openDateDetailModal,
    closeDateDetailModal,
    openDoctorSelectDialog,
    closeDoctorSelectDialog,
    openImportDialog,
    closeImportDialog,
    openDateTimePickerDialog,
    closeDateTimePickerDialog,
    openFixedShiftConfirmDialog,
    closeFixedShiftConfirmDialog,
    openFixedShiftCloseDialog,
    closeFixedShiftCloseDialog,
    openUrgentSettingsModal,
    closeUrgentSettingsModal,
    openCopyDialog,
    closeCopyDialog,

    // 急募一覧・詳細
    fetchUrgentTargets,
    openUrgentTargetListModal,
    closeUrgentTargetListModal,
    openUrgentTargetDetail,
    closeUrgentTargetDetail,
    confirmDoctorDirectly,
    cancelUrgentTarget,
    cancelApplication,

    // 急募設定保存
    saveIncentiveSettings,
    saveSelectedUrgentTargetSettings,

    // ユーティリティ
    clearError,
  };
};
